import alert_mixin from '@/utils/mixins/alert_mixin';
import { fetch_form_structure } from '@/services/main_section_service';
import { fetch_form } from '@/services/form_service';
import { fetch_dependent_questions } from '@/services/question_service';
import { extract_error_message } from '@/utils/api_response_helpers';

function first_defined_value(...values) {
  return values
    .find((value) => value !== null && value !== undefined);
}

function resolve_question_text(question) {
  return first_defined_value(question?.question_text, question?.question, question?.text, question?.name);
}

function resolve_question_type(question) {
  return first_defined_value(
    question?.question_type?.question_type,
    question?.question_type?.type,
    question?.question_type,
    question?.type
  );
}

function resolve_dependent_question_type(question) {
  return first_defined_value(resolve_question_type(question), question?.formatted_type);
}

function resolve_question_id(question) {
  return first_defined_value(question?.question_id, question?.id);
}

function resolve_sub_section_id(section) {
  return first_defined_value(section?.section_id, section?.section, section?.id);
}

function resolve_main_section_id(section) {
  return first_defined_value(section?.main_section_id, section?.main_section, section?.id);
}

function resolve_sub_section_name(section) {
  return first_defined_value(section?.section_name, section?.name);
}

function resolve_main_section_name(section) {
  return first_defined_value(section?.main_section_name, section?.name);
}

function string_or_fallback(value, fallback) {
  return String(value ?? '').trim() || fallback;
}

function format_type_label(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function fallback_id(prefix, index) {
  return `${prefix}-${index}`;
}

function normalized_order_number(source, index) {
  return Number(source?.order_number ?? index + 1);
}

function sort_by_order_number(items) {
  return items.sort((left, right) => Number(left.order_number) - Number(right.order_number));
}

function array_or_empty(value) {
  return Array.isArray(value) ? value : [];
}

function resolve_dependent_questions(question) {
  return array_or_empty(question?.dependent_questions).length
    ? question.dependent_questions
    : array_or_empty(question?.dependant_questions);
}

function has_question_text(question) {
  return Boolean(string_or_fallback(resolve_question_text(question), ''));
}

export default {
  name: 'PreviewForm',
  mixins: [alert_mixin],
  data: () => ({
    topBar: {
      title: 'Form Preview',
      exitLabel: 'Exit Preview',
    },
    formMeta: {
      eyebrow: 'Preview',
    },
    preview_loading: false,
    preview_error: '',
    preview_sections: [],
    form_instructions: [],
    active_main_section_index: 0,
  }),
  computed: {
    form_name() {
      return String(this.$route?.query?.form_name || '').trim() || 'Untitled Form';
    },
    form_id() {
      return Number(this.$route?.query?.form_id ?? null) || null;
    },
    total_main_sections() {
      return this.preview_sections.length;
    },
    active_main_section() {
      return this.preview_sections[this.active_main_section_index] || null;
    },
    section_progress_percent() {
      if (!this.total_main_sections) {
        return 0;
      }

      return Math.round(((this.active_main_section_index + 1) / this.total_main_sections) * 100);
    },
    section_title() {
      if (!this.active_main_section) {
        return 'No sections available';
      }

      return `Section ${this.active_main_section_index + 1}: ${this.active_main_section.main_section_name}`;
    },
    section_label() {
      if (!this.total_main_sections) {
        return 'Section 0 of 0';
      }

      return `Section ${this.active_main_section_index + 1} of ${this.total_main_sections}`;
    },
    next_button_label() {
      return this.active_main_section_index >= this.total_main_sections - 1
        ? 'End Of Preview'
        : 'Next Section';
    },
  },
  async created() {
    await this.load_form_preview();
  },
  methods: {
    normalize_dependent_question(question, index = 0) {
      const fallback_text = `Dependent Question ${index + 1}`;
      const question_text = resolve_question_text(question);
      const question_type = resolve_dependent_question_type(question);

      return {
        id: resolve_question_id(question) ?? fallback_id('dependent-question', index),
        text: string_or_fallback(question_text, fallback_text),
        type_label: format_type_label(question_type),
        trigger_condition: String(question?.trigger_condition ?? '').trim(),
        is_mandatory: typeof question?.is_mandatory === 'boolean' ? question.is_mandatory : null,
      };
    },
    normalize_question(question, index = 0) {
      const fallback_text = `Question ${index + 1}`;
      const question_text = resolve_question_text(question);
      const question_type = resolve_question_type(question);
      const dependent_questions = resolve_dependent_questions(question);
      const dependents_count_hint = dependent_questions.length;
      const embedded_dependents_have_text = dependent_questions.every(has_question_text);

      return {
        id: resolve_question_id(question) ?? fallback_id('question', index),
        question_number: question?.question_number ?? index + 1,
        text: string_or_fallback(question_text, fallback_text),
        type_label: format_type_label(question_type),
        dependent_questions: embedded_dependents_have_text
          ? dependent_questions.map((dep, dep_index) => this.normalize_dependent_question(dep, dep_index))
          : [],
        dependents_count_hint,
        dependents_loaded: embedded_dependents_have_text && dependents_count_hint > 0,
        dependents_loading: false,
        show_dependents: false,
      };
    },
    normalize_sub_section(sub_section, index = 0) {
      const section_id = resolve_sub_section_id(sub_section);
      const fallback_name = `Sub Section ${index + 1}`;
      const questions = array_or_empty(sub_section?.questions)
        .map((question, question_index) => this.normalize_question(question, question_index));

      return {
        id: section_id ?? fallback_id('sub-section', index),
        section_id: section_id ?? null,
        section_name: string_or_fallback(resolve_sub_section_name(sub_section), fallback_name),
        order_number: normalized_order_number(sub_section, index),
        form_instructions: array_or_empty(sub_section?.form_instructions),
        questions,
      };
    },
    normalize_main_section(main_section, index = 0) {
      const main_section_id = resolve_main_section_id(main_section);
      const fallback_name = `Main Section ${index + 1}`;
      const sub_sections = array_or_empty(main_section?.sub_sections)
        .map((sub_section, sub_index) => this.normalize_sub_section(sub_section, sub_index));

      return {
        id: main_section_id ?? fallback_id('main-section', index),
        main_section_id: main_section_id ?? null,
        main_section_name: string_or_fallback(resolve_main_section_name(main_section), fallback_name),
        description: main_section?.description ?? '',
        order_number: normalized_order_number(main_section, index),
        sub_sections: sort_by_order_number(sub_sections),
      };
    },
    async load_form_preview() {
      if (!this.form_id) {
        this.preview_error = 'No form was selected for preview.';
        return;
      }

      this.preview_loading = true;
      this.preview_error = '';

      try {
        const [items, form] = await Promise.all([
          fetch_form_structure(this.form_id),
          fetch_form(this.form_id),
        ]);
        this.form_instructions = array_or_empty(form?.form_instructions);
        this.preview_sections = sort_by_order_number(items
          .map((item, index) => this.normalize_main_section(item, index))
        );
        this.active_main_section_index = 0;
      } catch (error) {
        const message = extract_error_message(error, 'Failed to load the form preview.');
        this.preview_error = message;
        this.show_error(message);
      } finally {
        this.preview_loading = false;
      }
    },
    go_to_next_section() {
      if (this.active_main_section_index >= this.total_main_sections - 1) {
        return;
      }

      this.active_main_section_index += 1;
    },
    go_to_previous_section() {
      if (this.active_main_section_index <= 0) {
        return;
      }

      this.active_main_section_index -= 1;
    },
    async toggle_question_dependents(question) {
      if (!question) {
        return;
      }

      const next_state = !question.show_dependents;
      question.show_dependents = next_state;

      if (!next_state) {
        return;
      }

      if (question.dependents_loaded) {
        return;
      }

      const main_question_id = Number(question.id);

      if (!main_question_id) {
        return;
      }

      question.dependents_loading = true;

      try {
        const items = await fetch_dependent_questions(main_question_id);
        question.dependent_questions = Array.isArray(items)
          ? items.map((dep, dep_index) => this.normalize_dependent_question(dep, dep_index))
          : [];
        question.dependents_count_hint = question.dependent_questions.length;
        question.dependents_loaded = true;
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to load dependent questions.'));
      } finally {
        question.dependents_loading = false;
      }
    },
  },
};
